import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert
} from '@mui/material';
import {
    Upload as UploadIcon,
    Save as SaveIcon,
    Add as AddIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { getApiUrl } from '../../../config';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getPdfTemplateConfig, savePdfTemplateConfig, uploadPdfTemplate } from '../../../redux/ducks/invoices';
import FieldZone from './FieldZone';
import FieldConfigPanel from './FieldConfigPanel';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const AVAILABLE_FIELDS = [
    { key: 'invoiceNumber', label: 'Invoice Number', type: 'text', defaultWidth: 150, defaultHeight: 20 },
    { key: 'client.name', label: 'Client Name', type: 'text', defaultWidth: 200, defaultHeight: 20 },
    { key: 'client.email', label: 'Client Email', type: 'text', defaultWidth: 200, defaultHeight: 20 },
    { key: 'client.phone', label: 'Client Phone', type: 'text', defaultWidth: 150, defaultHeight: 20 },
    { key: 'invoiceDate', label: 'Invoice Date', type: 'date', defaultWidth: 120, defaultHeight: 20 },
    { key: 'dueDate', label: 'Due Date', type: 'date', defaultWidth: 120, defaultHeight: 20 },
    { key: 'billingPeriod', label: 'Billing Period', type: 'text', defaultWidth: 200, defaultHeight: 20 },
    { key: 'status', label: 'Status', type: 'text', defaultWidth: 100, defaultHeight: 20 },
    { key: 'subtotal', label: 'Subtotal', type: 'currency', defaultWidth: 100, defaultHeight: 20 },
    { key: 'taxRate', label: 'Tax Rate', type: 'text', defaultWidth: 80, defaultHeight: 20 },
    { key: 'taxAmount', label: 'Tax Amount', type: 'currency', defaultWidth: 100, defaultHeight: 20 },
    { key: 'amount', label: 'Total Amount', type: 'currency', defaultWidth: 100, defaultHeight: 20 },
    { key: 'balanceDue', label: 'Balance Due', type: 'currency', defaultWidth: 100, defaultHeight: 20 },
    { key: 'notes', label: 'Notes', type: 'multiline', defaultWidth: 400, defaultHeight: 60 },
    { key: 'account.name', label: 'Account Name', type: 'text', defaultWidth: 200, defaultHeight: 20 },
    { key: 'lineItems', label: 'Line Items Table', type: 'table', defaultWidth: 500, defaultHeight: 300 },
    { key: 'payments', label: 'Payments Table', type: 'table', defaultWidth: 400, defaultHeight: 100 },
];

const DEFAULT_TABLE_CONFIG = {
    lineItems: {
        headerFontSize: 10,
        headerFontWeight: 'bold',
        rowHeight: 18,
        headerHeight: 22,
        columns: [
            { key: 'description', label: 'Description', widthRatio: 0.35, alignment: 'left' },
            { key: 'billingType', label: 'Type', widthRatio: 0.15, alignment: 'center' },
            { key: 'hours', label: 'Hours', widthRatio: 0.15, alignment: 'right', format: 'hours' },
            { key: 'rate', label: 'Rate', widthRatio: 0.15, alignment: 'right', format: 'currency' },
            { key: 'amount', label: 'Amount', widthRatio: 0.20, alignment: 'right', format: 'currency' },
        ],
        showSubtotal: true,
        showTax: true,
        showTotal: true,
    },
    payments: {
        headerFontSize: 10,
        headerFontWeight: 'bold',
        rowHeight: 16,
        headerHeight: 20,
        columns: [
            { key: 'date', label: 'Date', widthRatio: 0.30, alignment: 'left', format: 'date' },
            { key: 'method', label: 'Method', widthRatio: 0.35, alignment: 'left' },
            { key: 'amount', label: 'Amount', widthRatio: 0.35, alignment: 'right', format: 'currency' },
        ],
        showSubtotal: false,
        showTax: false,
        showTotal: false,
    },
};

const DesignerContainer = styled(Box)({
    display: 'flex',
    gap: 16,
    minHeight: 600,
});

const CanvasPanel = styled(Box)(({ theme }) => ({
    flex: 1,
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'auto',
    backgroundColor: theme.palette.surface.inset,
}));

const PropertiesPanel = styled(Box)(({ theme }) => ({
    width: 320,
    flexShrink: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    maxHeight: 'calc(100vh - 200px)',
}));

const Toolbar = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
}));

const PdfWrapper = styled(Box)({
    position: 'relative',
    display: 'inline-block',
});

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const PdfLoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
}));

const AddFieldFormControl = styled(FormControl)({
    minWidth: 200,
});

const Spacer = styled(Box)({
    flex: 1,
});

const PlacingAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const EmptyTemplateBox = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    color: theme.palette.text.secondary,
}));

const InteractiveCanvas = styled(CanvasPanel, { shouldForwardProp: (prop) => prop !== 'placing' })(({ placing }) => ({
    cursor: placing ? 'crosshair' : 'default',
}));

const EmptySelectionBox = styled(Box)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const PdfTemplateDesigner = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const canvasRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [templateFile, setTemplateFile] = useState(null);
    const [templateUrl, setTemplateUrl] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageWidth, setPageWidth] = useState(612);
    const [pageHeight, setPageHeight] = useState(792);
    const [fields, setFields] = useState([]);
    const [selectedFieldId, setSelectedFieldId] = useState(null);
    const [addFieldKey, setAddFieldKey] = useState('');
    const [placingField, setPlacingField] = useState(null);
    const [defaults, setDefaults] = useState({
        fontFamily: 'Helvetica',
        fontSize: 10,
        fontColor: '#000000',
        currencySymbol: '$',
    });
    const [scaleFactor, setScaleFactor] = useState(1);

    const CANVAS_WIDTH = 650;

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = () => {
        setLoading(true);
        dispatch(getPdfTemplateConfig((resp) => {
            setLoading(false);
            if (resp?.ok) {
                const config = resp.data;
                if (config.templateFile?.storageKey) {
                    setTemplateFile(config.templateFile);
                    setTemplateUrl(getApiUrl() + '/billing/invoices/pdfTemplate/preview');
                    if (config.templateFile.pageWidth) setPageWidth(config.templateFile.pageWidth);
                    if (config.templateFile.pageHeight) setPageHeight(config.templateFile.pageHeight);
                }
                if (config.defaults) setDefaults(prev => ({ ...prev, ...config.defaults }));
                if (config.fields) setFields(config.fields);
            } else if (resp?.status !== 404) {
                dispatch(showAlert({ severity: 'error', message: 'Failed to load template configuration' }));
            }
        }));
    };

    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('template', file);
        dispatch(uploadPdfTemplate(formData, (resp) => {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            if (resp?.ok) {
                setTemplateFile(resp.data);
                setTemplateUrl(getApiUrl() + '/billing/invoices/pdfTemplate/preview?t=' + Date.now());
                if (resp.data.pageWidth) setPageWidth(resp.data.pageWidth);
                if (resp.data.pageHeight) setPageHeight(resp.data.pageHeight);
                dispatch(showAlert({ severity: 'success', message: 'Template uploaded successfully' }));
            }
        }));
    };

    const handleSave = () => {
        setSaving(true);
        dispatch(savePdfTemplateConfig({ defaults, fields }, (resp) => {
            setSaving(false);
            if (resp?.ok) {
                dispatch(showAlert({ severity: 'success', message: 'Template configuration saved' }));
            }
        }));
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const onPageLoadSuccess = (page) => {
        const scale = CANVAS_WIDTH / page.originalWidth;
        setScaleFactor(scale);
        setPageWidth(page.originalWidth);
        setPageHeight(page.originalHeight);
    };

    const handleAddField = () => {
        if (!addFieldKey) return;
        const fieldDef = AVAILABLE_FIELDS.find(f => f.key === addFieldKey);
        if (!fieldDef) return;

        if (fieldDef.type !== 'text') {
            const existing = fields.find(f => f.fieldKey === addFieldKey);
            if (existing) {
                setFields(prev => prev.filter(f => f.id !== existing.id));
                if (selectedFieldId === existing.id) setSelectedFieldId(null);
            }
        }

        setPlacingField(fieldDef);
    };

    const handleCanvasClick = useCallback((e) => {
        if (!placingField) return;

        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;

        const screenX = e.clientX - rect.left + (canvasRef.current?.scrollLeft || 0);
        const screenY = e.clientY - rect.top + (canvasRef.current?.scrollTop || 0);

        const pdfX = screenX / scaleFactor;
        const pdfY = pageHeight - (screenY / scaleFactor);

        const newField = {
            id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            fieldKey: placingField.key,
            label: placingField.label,
            type: placingField.type,
            page: 0,
            x: pdfX,
            y: pdfY,
            width: placingField.defaultWidth,
            height: placingField.defaultHeight,
            fontSize: defaults.fontSize,
            fontWeight: 'normal',
            alignment: 'left',
            prefix: '',
            suffix: '',
            fontColor: defaults.fontColor,
        };

        if (placingField.type === 'table') {
            newField.tableConfig = DEFAULT_TABLE_CONFIG[placingField.key] || DEFAULT_TABLE_CONFIG.lineItems;
        }

        if (placingField.type === 'multiline') {
            newField.lineSpacing = defaults.fontSize + 4;
        }

        setFields(prev => [...prev, newField]);
        setSelectedFieldId(newField.id);
        setPlacingField(null);
        setAddFieldKey('');
    }, [placingField, scaleFactor, pageHeight, defaults]);

    const handleFieldMove = useCallback((fieldId, newScreenX, newScreenY) => {
        setFields(prev => prev.map(f => {
            if (f.id !== fieldId) return f;
            const pdfX = newScreenX / scaleFactor;
            const pdfY = pageHeight - (newScreenY / scaleFactor);
            return { ...f, x: pdfX, y: pdfY };
        }));
    }, [scaleFactor, pageHeight]);

    const handleFieldUpdate = useCallback((fieldId, updates) => {
        setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f));
    }, []);

    const handleFieldDelete = useCallback((fieldId) => {
        setFields(prev => prev.filter(f => f.id !== fieldId));
        if (selectedFieldId === fieldId) setSelectedFieldId(null);
    }, [selectedFieldId]);

    const selectedField = fields.find(f => f.id === selectedFieldId);

    const availableForAdd = AVAILABLE_FIELDS;

    const pdfFile = useMemo(
        () => (templateUrl ? { url: templateUrl, withCredentials: true } : null),
        [templateUrl]
    );

    if (loading) {
        return (
            <LoadingBox>
                <CircularProgress />
            </LoadingBox>
        );
    }

    return (
        <Box>
            <Toolbar>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleUpload}
                />
                <Button
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={18} /> : <UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                >
                    {templateFile ? 'Replace Template' : 'Upload Template'}
                </Button>

                {templateFile && (
                    <>
                        <AddFieldFormControl size="small">
                            <InputLabel>Add Field</InputLabel>
                            <Select
                                value={addFieldKey}
                                onChange={(e) => setAddFieldKey(e.target.value)}
                                label="Add Field"
                            >
                                {availableForAdd.map(f => (
                                    <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                                ))}
                            </Select>
                        </AddFieldFormControl>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddField}
                            disabled={!addFieldKey}
                        >
                            Place Field
                        </Button>
                    </>
                )}

                <Spacer />

                <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || !templateFile}
                >
                    {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
            </Toolbar>

            {placingField && (
                <PlacingAlert severity="info">
                    Click on the template to place "{placingField.label}". Press Escape to cancel.
                </PlacingAlert>
            )}

            {!templateFile ? (
                <EmptyTemplateBox>
                    <Typography variant="h6" gutterBottom>No template uploaded</Typography>
                    <Typography variant="body2">
                        Upload a PDF template to get started. The template should contain your company branding, headers, and any static content. Dynamic invoice data will be overlaid at the positions you define.
                    </Typography>
                </EmptyTemplateBox>
            ) : (
                <DesignerContainer>
                    <InteractiveCanvas
                        ref={canvasRef}
                        onClick={handleCanvasClick}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                setPlacingField(null);
                                setAddFieldKey('');
                            }
                        }}
                        tabIndex={0}
                        placing={!!placingField}
                    >
                        <PdfWrapper>
                            <Document
                                file={pdfFile}
                                onLoadSuccess={onDocumentLoadSuccess}
                                loading={<PdfLoadingBox><CircularProgress /></PdfLoadingBox>}
                            >
                                <Page
                                    pageNumber={1}
                                    width={CANVAS_WIDTH}
                                    onLoadSuccess={onPageLoadSuccess}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                />
                            </Document>

                            {fields.filter(f => f.page === 0).map(field => (
                                <FieldZone
                                    key={field.id}
                                    field={field}
                                    scaleFactor={scaleFactor}
                                    pageHeight={pageHeight}
                                    isSelected={field.id === selectedFieldId}
                                    onSelect={() => setSelectedFieldId(field.id)}
                                    onMove={handleFieldMove}
                                />
                            ))}
                        </PdfWrapper>
                    </InteractiveCanvas>

                    <PropertiesPanel>
                        {selectedField ? (
                            <FieldConfigPanel
                                field={selectedField}
                                onUpdate={(updates) => handleFieldUpdate(selectedField.id, updates)}
                                onDelete={() => handleFieldDelete(selectedField.id)}
                                availableFields={AVAILABLE_FIELDS}
                            />
                        ) : (
                            <EmptySelectionBox>
                                <Typography variant="body2">
                                    Select a field on the template to edit its properties, or add a new field using the toolbar above.
                                </Typography>
                            </EmptySelectionBox>
                        )}
                    </PropertiesPanel>
                </DesignerContainer>
            )}
        </Box>
    );
};

export default PdfTemplateDesigner;
